'use client';

import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { usePlausible } from 'next-plausible';
import { TouchEvent, useEffect, useRef, useState } from 'react';

import FaviconImage from '@/components/FaviconImage';

import styles from './homepage-editorial.module.css';
import {
  productDescription,
  productName,
  productTitle,
  type HomepageProduct,
} from './types';

const AUTOPLAY_DELAY_MS = 6500;

type SponsorShowcaseItem = {
  product: HomepageProduct;
  referralUrl: string;
};

type SponsorEvents = {
  'Homepage Sponsor Impression': {
    productId: number;
    productName: string;
    position: number;
    locale: string;
  };
  'Homepage Sponsor Click': {
    productId: number;
    productName: string;
    position: number;
    locale: string;
  };
};

const SHOWCASE_LABELS: Record<string, {
  visit: string;
  previous: string;
  next: string;
}> = {
  en: { visit: 'Visit website', previous: 'Previous sponsor', next: 'Next sponsor' },
  zh: { visit: '访问官方网站', previous: '上一个赞助产品', next: '下一个赞助产品' },
  ja: { visit: '公式サイトを見る', previous: '前のスポンサー', next: '次のスポンサー' },
  ko: { visit: '공식 웹사이트 방문', previous: '이전 스폰서', next: '다음 스폰서' },
  de: { visit: 'Website besuchen', previous: 'Vorheriger Sponsor', next: 'Nächster Sponsor' },
  es: { visit: 'Visitar sitio web', previous: 'Patrocinador anterior', next: 'Siguiente patrocinador' },
  fr: { visit: 'Visiter le site', previous: 'Sponsor précédent', next: 'Sponsor suivant' },
  pl: { visit: 'Odwiedź stronę', previous: 'Poprzedni sponsor', next: 'Następny sponsor' },
  nl: { visit: 'Website bezoeken', previous: 'Vorige sponsor', next: 'Volgende sponsor' },
  ru: { visit: 'Перейти на сайт', previous: 'Предыдущий спонсор', next: 'Следующий спонсор' },
};

export default function HomepageSponsorShowcase({
  items,
  locale,
  sponsoredLabel,
}: {
  items: SponsorShowcaseItem[];
  locale: string;
  sponsoredLabel: string;
}) {
  const labels = SHOWCASE_LABELS[locale] || SHOWCASE_LABELS.en;
  const visibleItems = items;
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const showcaseRef = useRef<HTMLElement>(null);
  const trackedImpressions = useRef(new Set<number>());
  const plausible = usePlausible<SponsorEvents>();

  const itemCount = visibleItems.length;
  const activeItem = visibleItems[activeIndex];

  useEffect(() => {
    if (activeIndex < itemCount) return;
    setActiveIndex(0);
  }, [activeIndex, itemCount]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReduceMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  useEffect(() => {
    const element = showcaseRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting && entry.intersectionRatio >= 0.5),
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (itemCount <= 1 || paused || reduceMotion || !isVisible) return;
    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % itemCount);
    }, AUTOPLAY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, isVisible, itemCount, paused, reduceMotion]);

  useEffect(() => {
    if (!activeItem || !isVisible || trackedImpressions.current.has(activeItem.product.id)) return;
    const timer = window.setTimeout(() => {
      if (trackedImpressions.current.has(activeItem.product.id)) return;
      trackedImpressions.current.add(activeItem.product.id);
      plausible('Homepage Sponsor Impression', {
        props: {
          productId: activeItem.product.id,
          productName: productName(activeItem.product),
          position: activeIndex + 1,
          locale,
        },
      });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [activeIndex, activeItem, isVisible, locale, plausible]);

  if (!activeItem) return null;

  const name = productName(activeItem.product);
  const title = productTitle(activeItem.product);
  const showPrevious = () => setActiveIndex((current) => (current - 1 + itemCount) % itemCount);
  const showNext = () => setActiveIndex((current) => (current + 1) % itemCount);

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    const start = touchStartX.current;
    const end = event.changedTouches[0]?.clientX;
    touchStartX.current = null;
    if (start == null || end == null || Math.abs(start - end) < 44 || itemCount <= 1) return;
    if (start > end) showNext();
    else showPrevious();
  };

  const trackClick = () => {
    plausible('Homepage Sponsor Click', {
      props: {
        productId: activeItem.product.id,
        productName: name,
        position: activeIndex + 1,
        locale,
      },
    });
  };

  return (
    <section
      ref={showcaseRef}
      className={styles.sponsorShowcase}
      aria-label={`${sponsoredLabel}: ${name}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        const nextTarget = event.relatedTarget;
        if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
          setPaused(false);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.sponsorViewport}>
        <article key={activeItem.product.id} className={styles.sponsorSlide}>
          <div className={styles.sponsorSlideCopy}>
            <div className={styles.sponsorMeta}>
              <span className={styles.sponsorLabel}>{sponsoredLabel}</span>
              {activeItem.product.category_name && (
                <span className={styles.sponsorCategory}>{activeItem.product.category_name}</span>
              )}
            </div>
            <div className={styles.sponsorIdentity}>
              <FaviconImage
                website={activeItem.product.link}
                name={name}
                width={48}
                height={48}
                className={styles.sponsorLogo}
              />
              <div className={styles.sponsorText}>
                <h2>{name}</h2>
                {title && title !== name && <p className={styles.sponsorTitle}>{title}</p>}
                <p className={styles.sponsorDescription}>{productDescription(activeItem.product)}</p>
              </div>
            </div>
          </div>

          <div className={styles.sponsorActions}>
            {itemCount > 1 && (
              <div className={styles.sponsorControls} role="group" aria-label="Sponsor carousel controls">
                <button
                  type="button"
                  className={styles.sponsorArrow}
                  aria-label={labels.previous}
                  onClick={showPrevious}
                >
                  <ChevronLeft size={19} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={styles.sponsorArrow}
                  aria-label={labels.next}
                  onClick={showNext}
                >
                  <ChevronRight size={19} aria-hidden="true" />
                </button>
              </div>
            )}
            <a
              className={styles.sponsorCta}
              href={activeItem.referralUrl}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
              onClick={trackClick}
            >
              {labels.visit}
              <ExternalLink size={17} aria-hidden="true" />
            </a>
          </div>
        </article>
      </div>
    </section>
  );
}
